import React, {useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import __ from "lodash";
import moment from "moment";
import {FormControl, FormGroup, Grid, Tooltip, IconButton, Icon, TextField, useTheme} from "@material-ui/core";
import { FormDialog, LabelledPaper, MaskedText } from '../../../controls';
import { populateTemplate } from "../../../functions/PopulateTemplate";
import SendCateringOrderValidator from "../validators/SendCateringOrderValidator";
import { useSendCateringOrder } from "../../../services/flights";
import { useCompanyContacts } from "../../../services/companies";
import OrderView from "./CateringOrderView";
import { getRecipientList } from "../../../functions/TemplateHelperFunctions";

const paperProps = {
  style: {
    backgroundColor: "#f8f8f8",
    boxShadow: 'none',
  }
}

const defaultLabelledPaperProps = {
  checkChildren: true,
  style: {display: "block", border: "solid 1px #b9b9b9" },
  labelStyle: {background: "#f0f0f0"},
  collapsible: false,
}

const prepareLines = (lines) => {

  if (lines && Array.isArray(lines)) {
    lines.forEach(line => {
      line["arrivalDate"] = moment(line.arrivalDate).format("YYYY-MM-DD");
      line["departureDate"] = moment(line.departureDate).format("YYYY-MM-DD");
    })
  }
  return lines;
}

const SendCateringOrderDialog = (_props) => {
  const props = {
    canEdit: false,
    ..._props,
  }

  const SystemData = props.SystemData;
  const theme = useTheme();
  const extraKeys = props.extraKeys;
  const formatChars = props.formatChars;

  const [dialogStates, setDialogStates] = useState(props.dialogStates);
  const [requestSent, setRequestSent] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    severity: "error",
    message: "",
  });

  let companyContactsData = null;
  const open = (record, title) => {
    setDialogStates({...dialogStates, showDialog:true, record, title});
  }
  const close = () => {
    setDialogStates({...dialogStates, showDialog:false, record: {}, title: 'Send Catering Order'});
    companyContactsData = null;
    queryClient.removeQueries(["CompanyContacts"]);
  }

  const queryClient = useQueryClient();
  const contactExtraKeys = [dialogStates.record?.supplierUuid ? dialogStates.record.supplierUuid : ""];
  const companyContacts = useCompanyContacts({extraKeys: contactExtraKeys})
  const handleUpdateOrder = useSendCateringOrder();

  companyContactsData = companyContacts.isSuccess ? companyContacts.data : null;

  const getButtons = () => {
    if (requestSent) {
      return (
        {
          save:{icon: "send", key: "Save", label: "Re-Send", color: "primary", disabled: (status)=>{return status.error}},
          cancel:{icon: "cancel", key: "Cancel", label: "Close", color: "primary"},
        }
      );
    }
    return (
      {
        save:{icon: "send", key: "Save", label: "Send", color: "primary", disabled: (status)=>{return status.error}},
        cancel:{icon: "cancel", key: "Cancel", label: "Cancel", color: "primary"}
      }
    );
  }

  if (dialogStates.showDialog && companyContactsData) {
    dialogStates.record["orderEmail"] = getRecipientList(companyContactsData, "CATERING", dialogStates?.record?.airportCode);
    return (
      <div key="formContainer" style={{width: "100%", textAlign:"left" }}>
        <FormDialog
          key="send catering order"
          title={dialogStates.title}
          message=""
          open={dialogStates.showDialog}
          dialogOptions={{maxWidth: "lg", fullWidth: true, PaperProps: paperProps }}
          record={dialogStates.record}
          validator={SendCateringOrderValidator}
          validatorPosition="top"
          alert={alert}
          formHeightOffset={320}
          titleBarStyle={{backgroundColor: '#3f3f3f', color: theme.palette.primary.main}}
          //buttonBarStyleOverride={{position: "relative", width: "calc(100% - 15px)", backgroundColor: "#ffffff", borderTop: `1px solid ${props.fullPageStyle?"grey":"lightgrey"}`, ...props.fullPageStyle}}
          //formBodyStyle={{...props.fullPageStyle}}
          //style={{...props.fullPageStyle}}
          buttons={getButtons()}
          onChange={(state) => {
            if (state.buttonPressed === "cancel") {
              close();
              if (props.close)
                props.close();
            }
            if (state.buttonPressed === "save") {
              SystemData.toggleLoadingProgress(true);

              const orderContent = populateTemplate(state.record.orderTemplate, state.record, state.record.lines || []);
              const updatedRecord = {...state.record, status: "REQUESTED", orderContent};

              handleUpdateOrder.mutate({
                extraKeys,
                record: updatedRecord,
                postSuccess: () => {
                  setRequestSent(true);
                  SystemData.toggleLoadingProgress(false);
                  setAlert({ severity: "info", open: true, message: `Catering Order sent`, timeOut: 5 });
                  queryClient.invalidateQueries(["CateringOrders"]);
                },
                postError: (data) => {
                  SystemData.toggleLoadingProgress(false);
                  if (data.errorMessage) {
                    SystemData.alert("Error", `${data.errorMessage}`);
                  } else {
                    SystemData.alert("Error", `An Unexpected Error has occurred sending the Catering Order`);
                  }
                }
              });

            } else {
              console.log("Unhandled FormDialog onChange");
            }
          }}
        >
          <Grid container checkChildren={true} spacing={3}>
            <Grid item xs={12} checkChildren={true} row>
              <LabelledPaper {...defaultLabelledPaperProps} label="" >
                <FormControl checkChildren={true} key="FC-addresses" style={{display: "block" }}>
                  <FormGroup checkChildren={true} key="FG-addresses" style={{padding: "8px 8px 8px 8px"}}>
                    <Grid container checkChildren={true} spacing={3}>

                      <Grid item xs={12} checkChildren={true}>
                        <div style={{display: "flex"}}>
                          <MaskedText
                            id="orderEmail"
                            label="To"
                            inputLabelProps={{ shrink: true }}
                            style={{width: "100%"}}
                            mask={"x".repeat(200)}
                            maskChar={""}
                            alwaysShowMask={false}
                            formatChars={formatChars}
                            forceUpperCase={false}
                          />
                          {(record, changeRecord) => (
                            <Tooltip title="Restore default recipients">
                              <IconButton
                                style={{padding:2}}
                                disabled={ record.orderEmail === getRecipientList(companyContactsData, "CATERING", dialogStates?.record?.airportCode)  }
                                onClick={()=> {
                                  changeRecord({
                                    orderEmail: getRecipientList(companyContactsData, "CATERING", dialogStates?.record?.airportCode)
                                  })
                                }}><Icon>restore</Icon></IconButton>
                            </Tooltip>
                          )}
                        </div>
                      </Grid>
                      <Grid item xs={12} checkChildren={true}>
                        <MaskedText
                          id="carbonCopy"
                          label="CC"
                          inputLabelProps={{ shrink: true }}
                          style={{width: "100%"}}
                          mask={"x".repeat(200)}
                          maskChar={""}
                          alwaysShowMask={false}
                          formatChars={formatChars}
                          forceUpperCase={false}
                        />
                      </Grid>
                      <Grid item xs={12} checkChildren={true}>
                        <MaskedText
                          id="orderSubject"
                          label="Subject"
                          inputLabelProps={{ shrink: true }}
                          style={{width: "100%"}}
                          mask={"x".repeat(200)}
                          maskChar={""}
                          alwaysShowMask={false}
                          formatChars={formatChars}
                          forceUpperCase={false}
                        />
                      </Grid>
                      <Grid item xs={12} checkChildren={true}>
                        <TextField
                          id="messageAdditionalInfo"
                          label="Additional Information"
                          InputLabelProps={{ shrink: true }}
                          style={{width: "100%"}}
                          multiline
                          onChange={(value, props) => {
                            let additionalInfo = "";
                            if (value && value.length > 0) {
                              additionalInfo = value;
                            }
                            props.recordChange({...props.record, additionalInfo: additionalInfo, messageAdditionalInfo: value});
                          }}
                        />
                      </Grid>
                    </Grid>
                  </FormGroup>
                </FormControl>
              </LabelledPaper>
            </Grid>

            {record => {
              return (
                <Grid item xs={12} checkChildren={true}>
                  <LabelledPaper {...defaultLabelledPaperProps} label={`Message Body`} >
                    <FormControl checkChildren={true} key="FC-slotmessage" style={{display: "block" }}>
                      <FormGroup checkChildren={true} key="FG-slotmessage" style={{padding: "8px 8px 8px 8px"}}>
                        <OrderView order={record} suppressTitle/>
                      </FormGroup>
                    </FormControl>
                  </LabelledPaper>
                </Grid>
              )}}
          </Grid>
        </FormDialog>
      </div>
    );
  }
  return (<></>);
}

export default SendCateringOrderDialog;
